/**
 * WorkflowChangeNotice — what a non-admin member is told after their
 * organization's workflow is changed (DC-1802, AC bullet 2).
 *
 * The 2026-08-17 incident's worst half was not the flip. It was that every
 * non-admin in the flipped organization opened the product, found the
 * surfaces they used every day simply gone, and had nothing to read: no
 * statement that anything had been changed, no author, no date, and no idea
 * who could put it back. An unexplained shell is indistinguishable from a
 * broken one, and it generates an incident instead of a question.
 *
 * This states the three facts that turn it back into a question — what
 * happened, who did it, and which role can undo it — and nothing else. It
 * offers no action, because a non-admin has none to take here; the recovery
 * path is a person, not a button.
 *
 * Deliberately NOT shown to administrators: they own the control, and the
 * same provenance already renders beside it in Settings.
 *
 * Bounded by WORKFLOW_CHANGE_NOTICE_WINDOW_DAYS rather than shown forever.
 * A change from months ago is the status quo, not news, and a banner that
 * never leaves is one people stop reading. The window is a product choice,
 * not a technical constraint — it is a single named constant so it can move.
 */

import React from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useOrganizationWorkflowContext } from '../../hooks/useOrganizationWorkflowContext';
import {
  formatChangedAt,
  formatChangedBy,
  isWithinNoticeWindow,
} from '../../config/workflowContextCopy';

export const WorkflowChangeNotice: React.FC = () => {
  const { hasRole } = useAuth();
  const workflowContext = useOrganizationWorkflowContext();

  // Administrators have the control and its provenance in Settings.
  if (hasRole('admin')) return null;
  if (workflowContext.status !== 'ready') return null;

  const { provenance } = workflowContext.context;
  if (!provenance) return null;
  if (!isWithinNoticeWindow(provenance.changedAt)) return null;

  return (
    <aside
      className="workflow-change-notice"
      data-testid="workflow-change-notice"
      role="status"
    >
      <p>
        Your organization&rsquo;s workspace starting point was changed by{' '}
        <strong>{formatChangedBy(provenance)}</strong> on{' '}
        {formatChangedAt(provenance.changedAt)}. That is why the sections
        available to you have changed.
      </p>
      <p className="workflow-change-notice-recovery">
        Nothing has been deleted. An <strong>organization administrator</strong>{' '}
        can change the workspace starting point back, in Settings.
      </p>
    </aside>
  );
};

export default WorkflowChangeNotice;
